import React, { useEffect, useState } from 'react';
import { useDocs } from '../../../../../../../hooks/useDocs';
import { hasLegacyMissionContent } from '../../../../../../../utils/missionDocMigration';
import TitleArea from '../TitleArea';
import QuickNotesBox from '../QuickNotesBox';
import MissionDocSheet from './components/MissionDocSheet';
import AttachDocPicker from './components/AttachDocPicker';
import AttachedDocsList from './components/AttachedDocsList';
import { useMissionDocMigration } from './hooks/useMissionDocMigration';
import { useMissionDocNavigation } from './hooks/useMissionDocNavigation';
import { useAttachedDocsActions } from './hooks/useAttachedDocsActions';
import { useAttachedDocsData } from './hooks/useAttachedDocsData';

/**
 * Mission Full View main column: title, quick notes, and attached docs.
 * Real content lives in Doc records; opening one shows DocSheet in mission context.
 */
const AttachedDocsSection = ({
  task,
  taskId,
  taskName,
  taskIcon,
  onRenameTask,
  onIconChange,
  canViewAdminDocs,
  onAttachedDocsChange,
  onQuickNotesChange,
  onMissionDocsMigrated,
  onClearMissionLegacyNotes,
  onMissionDocTicksChange,
  onMissionDocFilesChange,
  onMissionVariablesChange,
  // Variable resolution context (doc pills resolve doc → campaign → parents)
  variableCampaign,
  allSprints,
  onCampaignVariablesChange,
  onPatchInheritedVariable,
  // Session restore from the owning tab
  initialDocStack = [],
  missionDocDirect = false,
  onDocStackChange,
}) => {
  const { docs: libraryDocs, allDocs, loading: libraryLoading, createDoc, reload } = useDocs({
    eager: false,
  });

  const { migrating, runMigration } = useMissionDocMigration(task, taskId, onMissionDocsMigrated);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const hasLegacyNotes = hasLegacyMissionContent(task);
  const alreadyTransferred = Boolean(task?.docsMigratedAt);

  const {
    docStack,
    activeDocId,
    sheetCloseRef,
    handleOpenDoc,
    handleBack,
    handleCloseSheet,
  } = useMissionDocNavigation(taskId, initialDocStack, onDocStackChange);

  useEffect(() => {
    if (activeDocId) reload({ silent: true });
  }, [activeDocId, reload]);

  const {
    attachPickerOpen,
    setAttachPickerOpen,
    attachQuery,
    setAttachQuery,
    creatingDoc,
    handleAttachExisting,
    handleDetach,
    handleCreateAndAttach,
  } = useAttachedDocsActions({
    taskId,
    taskName,
    taskIcon,
    attachedDocs: task?.attachedDocs,
    libraryDocs,
    createDoc,
    onAttachedDocsChange,
    handleOpenDoc,
  });

  const {
    docsById,
    resolveDocMeta,
    visibleAttached,
    attachableDocs,
  } = useAttachedDocsData({
    task,
    allDocs,
    libraryDocs,
    attachQuery,
    canViewAdminDocs,
  });

  return (
    <>
      {!activeDocId && (
      <section className="mfv-content mfv-content--home">
      <div className="mfv-content__wrapper">
        <div className="mfv-content__page">
          <TitleArea
            taskId={taskId}
            taskName={taskName}
            taskIcon={taskIcon}
            task={task}
            onRenameTask={onRenameTask}
            onIconChange={onIconChange}
          />

          <QuickNotesBox
            taskId={taskId}
            value={task?.quickNotes}
            onQuickNotesChange={onQuickNotesChange}
          />

          {hasLegacyNotes && (
            <div className="mfv-legacy-notes">
              <div className="mfv-legacy-notes__text">
                <strong>This mission has old notes.</strong>{' '}
                {alreadyTransferred
                  ? 'Already transferred to a doc — clear the leftover copy when you no longer need it.'
                  : 'Move them into a real doc, or clear them out.'}
              </div>
              <div className="mfv-legacy-notes__actions">
                {!alreadyTransferred && (
                  <button
                    type="button"
                    className="mfv-legacy-notes__btn mfv-legacy-notes__btn--primary"
                    onClick={runMigration}
                    disabled={migrating}
                  >
                    {migrating ? 'Transferring…' : 'Transfer to a doc'}
                  </button>
                )}
                {confirmingClear ? (
                  <>
                    <span className="mfv-legacy-notes__confirm-label">Clear permanently?</span>
                    <button
                      type="button"
                      className="mfv-legacy-notes__btn mfv-legacy-notes__btn--danger"
                      onClick={() => {
                        onClearMissionLegacyNotes?.(taskId);
                        setConfirmingClear(false);
                      }}
                    >
                      Yes, clear
                    </button>
                    <button
                      type="button"
                      className="mfv-legacy-notes__btn"
                      onClick={() => setConfirmingClear(false)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="mfv-legacy-notes__btn"
                    onClick={() => setConfirmingClear(true)}
                    disabled={migrating}
                  >
                    Clear old notes
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mfv-attached-docs">
            <div className="mfv-attached-docs__header">
              <span className="mfv-attached-docs__label">Docs</span>
              <div className="mfv-attached-docs__actions">
                <button
                  type="button"
                  className="mfv-attached-docs__action"
                  onClick={() => {
                    setAttachPickerOpen((v) => {
                      const next = !v;
                      if (next) reload();
                      return next;
                    });
                  }}
                >
                  Attach existing
                </button>
                <button
                  type="button"
                  className="mfv-attached-docs__action mfv-attached-docs__action--primary"
                  onClick={handleCreateAndAttach}
                  disabled={creatingDoc}
                >
                  {creatingDoc ? 'Creating…' : '+ New doc'}
                </button>
              </div>
            </div>

            {attachPickerOpen && (
              <AttachDocPicker
                attachQuery={attachQuery}
                setAttachQuery={setAttachQuery}
                setAttachPickerOpen={setAttachPickerOpen}
                libraryLoading={libraryLoading}
                attachableDocs={attachableDocs}
                onAttach={handleAttachExisting}
              />
            )}

            {visibleAttached.length === 0 ? (
              <div className="mfv-attached-docs__empty">
                No docs yet — attach an SOP from the library or start a fresh
                doc for this mission.
              </div>
            ) : (
              <AttachedDocsList
                visibleAttached={visibleAttached}
                docsById={docsById}
                libraryLoading={libraryLoading}
                onOpenDoc={handleOpenDoc}
                onDetach={handleDetach}
              />
            )}
          </div>
        </div>
      </div>
      </section>
      )}

      <MissionDocSheet
        activeDocId={activeDocId}
        docStack={docStack}
        docsById={docsById}
        task={task}
        taskId={taskId}
        taskName={taskName}
        libraryDocs={libraryDocs}
        libraryLoading={libraryLoading}
        sheetCloseRef={sheetCloseRef}
        handleBack={handleBack}
        handleCloseSheet={handleCloseSheet}
        missionDocDirect={missionDocDirect}
        handleOpenDoc={handleOpenDoc}
        resolveDocMeta={resolveDocMeta}
        onMissionDocTicksChange={onMissionDocTicksChange}
        onMissionDocFilesChange={onMissionDocFilesChange}
        onMissionVariablesChange={onMissionVariablesChange}
        variableCampaign={variableCampaign}
        allSprints={allSprints}
        onCampaignVariablesChange={onCampaignVariablesChange}
        onPatchInheritedVariable={onPatchInheritedVariable}
      />
    </>
  );
};

export default AttachedDocsSection;
